import { useEffect, useRef, useState, type MouseEvent } from 'react';

import { api } from '../../api';
import { Connection, TripSuggestion } from '../../types';
import { ANOTHER_HOTEL, END_HOTEL, TRIP_SUGGESTION, tripAnswerSaid } from '../../joining';
import { useUndoToast } from '../../ui';

// N42 (DECISION-080 §6) — the desk's half of *Not now*, *End* and the hotel's
// trip as a suggestion, shared by Today and My hotels. One tap each. The undo is
// the design system's toast (\`useUndoToast\`, generalised in the N42 fix round —
// design review SHOULD 1c): the answer, "{hotel} sees it when this closes", and
// *Undo*, over the 30-second person-window the API gives (\`undoSeconds\`, counted
// from the server's own clock — correctness CONSIDER 11); focus moves to *Undo*
// and comes back to the restored row. The window is rebuilt from the read too:
// leaving the page and coming back inside it keeps the Undo. The words are
// joining.ts's; the rules are the API's.

export type AnswerKind = 'not-now' | 'end';

const idOf = (c: Connection) => c.hotelAccountId ?? c.accountId ?? '';
const nameOf = (c: Connection) => c.name ?? c.hotelName ?? 'The hotel';

/** The id a row's focus target carries, so the undo can hand focus back to it. */
export const rowFocusId = (c: Connection) => `hotel-row-${idOf(c)}`;

/**
 * The answers given here, with their undo toast. \`hidden\` — the hotels whose
 * answer is still inside its window: their row leaves the list until it closes
 * or is undone. \`items\` is the connections read, from which an open window is
 * rebuilt (\`undoSeconds\`).
 */
export function useAnswers(onChanged: () => Promise<void>, items: Connection[] | null | undefined) {
  const toast = useUndoToast(() => void onChanged());
  const [hidden, setHidden] = useState<Record<string, number>>({});
  const shown = useRef(new Set<string>());

  const open = (kind: AnswerKind, c: Connection, undoSeconds: number) => {
    const id = idOf(c);
    const name = nameOf(c);
    const until = Date.now() + Math.max(0, undoSeconds) * 1000;
    shown.current.add(id);
    setHidden((h) => ({ ...h, [id]: until }));
    toast.showWith({
      title: kind === 'end' ? END_HOTEL.ended(name) : ANOTHER_HOTEL.saidNotNow(name),
      sub: kind === 'end' ? `${END_HOTEL.stays} ${ANOTHER_HOTEL.seesWhenClosed(name)}` : ANOTHER_HOTEL.seesWhenClosed(name),
      until,
      label: kind === 'end' ? END_HOTEL.undoLabel(name) : ANOTHER_HOTEL.undoLabel(name),
      onUndo: async () => {
        await api('POST', `/connections/${id}/undo`);
        setHidden((h) => {
          const next = { ...h };
          delete next[id];
          return next;
        });
      },
      returnFocus: () => requestAnimationFrame(() => document.getElementById(`hotel-row-${id}`)?.focus()),
    });
  };

  // A window still open in the read (the page was left and come back to): its toast again, once.
  useEffect(() => {
    for (const c of items ?? []) {
      const id = idOf(c);
      if ((c.status === 'declined' || c.status === 'ended') && (c.undoSeconds ?? 0) > 0 && !shown.current.has(id) && !toast.open) {
        open(c.status === 'ended' ? 'end' : 'not-now', c, c.undoSeconds!);
        break;
      }
    }
    // `open` and `toast` are recreated each render; the read is what changes.
  }, [items]);

  // A hidden row comes back once its window has closed (as history, by the read).
  useEffect(() => {
    const next = Object.values(hidden).filter((u) => u > Date.now()).sort((a, b) => a - b)[0];
    if (next === undefined) return;
    const t = window.setTimeout(() => {
      setHidden((h) => Object.fromEntries(Object.entries(h).filter(([, u]) => u > Date.now())));
      void onChanged();
    }, next - Date.now() + 250);
    return () => window.clearTimeout(t);
  }, [hidden]);

  const answer = async (kind: AnswerKind, c: Connection) => {
    const r = await api<{ undoUntil: string; undoSeconds?: number }>('POST', `/connections/${idOf(c)}/${kind}`);
    // An older API sends no seconds: the window its `undoUntil` gives.
    open(kind, c, r.undoSeconds ?? Math.max(0, Math.round((Date.parse(r.undoUntil) - Date.now()) / 1000)));
    await onChanged();
  };
  const isHidden = (c: Connection) => (hidden[idOf(c)] ?? 0) > Date.now();
  return { answer, isHidden, toast: toast.element };
}

/** A button that says not now (or ends) and hands the answer up; its error said under it. */
export function AnswerButton({ kind, c, onAnswer }: { kind: AnswerKind; c: Connection; onAnswer: (kind: AnswerKind, c: Connection) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const name = nameOf(c);
  const go = async (e: MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    setError('');
    try {
      await onAnswer(kind, c);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <>
      {/* End is destructive: outlined rust, never solid (the design system;
          design review SHOULD 6). Not now is a soft, re-askable answer. */}
      <button
        className={kind === 'end' ? 'btn danger-outline' : 'btn secondary'}
        style={{ marginLeft: 'var(--space-xs)' }}
        aria-label={kind === 'end' ? END_HOTEL.endLabel(name) : ANOTHER_HOTEL.notNowLabel(name)}
        disabled={busy}
        onClick={go}
      >
        {kind === 'end' ? (busy ? END_HOTEL.ending : END_HOTEL.end) : busy ? ANOTHER_HOTEL.saying : ANOTHER_HOTEL.notNow}
      </button>
      {error && (
        <p className="error-line" role="alert" style={{ marginTop: 'var(--space-xs)', whiteSpace: 'normal' }}>
          {error}
        </p>
      )}
    </>
  );
}

/**
 * A live hotel's offered trip, as a row of the desk's list: Keep mine / Add
 * theirs (Leave it / Add it) — two equal choices — with the new trip's name in
 * the row, theirs to change before they tap (George, N42 fix round), and the
 * read-back of what the SERVER recorded handed up (correctness MUST 1).
 */
export function TripRow({
  chipClass,
  chipText,
  title,
  meta,
  c,
  s,
  onAnswered,
}: {
  chipClass: string;
  chipText: string;
  title: string;
  meta: string;
  c: Connection;
  s: TripSuggestion;
  onAnswered: (said: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState<'' | 'keep' | 'add'>('');
  const [error, setError] = useState('');
  const [name, setName] = useState(s.suggestedName ?? s.trip.name);
  const hotel = nameOf(c);
  const fieldId = `trip-name-${idOf(c)}`;
  const answer = async (a: 'keep' | 'add') => {
    if (a === 'add' && !name.trim()) {
      setError(TRIP_SUGGESTION.nameNeeded);
      return;
    }
    setBusy(a);
    setError('');
    try {
      const body = a === 'add' ? { answer: 'add', name: name.trim() } : { answer: s.match === 'differs' ? 'keep' : 'leave' };
      const r = await api<{ answer: string; listed: boolean; name?: string }>('POST', `/connections/${idOf(c)}/trip`, body);
      await onAnswered(tripAnswerSaid(hotel, s, r));
    } catch (e) {
      setError((e as Error).message);
      setBusy('');
    }
  };
  return (
    <tr>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span className={`uchip ${chipClass}`}>{chipText}</span>
      </td>
      <td>
        {title}
        <br />
        <span className="hint">{meta}</span>
        <label htmlFor={fieldId} style={{ marginTop: 'var(--space-sm)' }}>
          {TRIP_SUGGESTION.nameLabel}
        </label>
        <input id={fieldId} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        {error && <p className="error-line" role="alert">{error}</p>}
      </td>
      <td className="num" style={{ whiteSpace: 'nowrap' }}>
        <button className="btn secondary" disabled={!!busy} onClick={() => void answer('keep')}>
          {busy === 'keep' ? TRIP_SUGGESTION.saving : TRIP_SUGGESTION.keep(s)}
        </button>
        <button className="btn secondary" style={{ marginLeft: 'var(--space-xs)' }} disabled={!!busy} onClick={() => void answer('add')}>
          {busy === 'add' ? TRIP_SUGGESTION.saving : TRIP_SUGGESTION.add(s)}
        </button>
      </td>
    </tr>
  );
}
